import {
  ArrowUpRight,
  Car,
  CaretLeft,
  CaretRight,
  FirstAidKit,
  GraduationCap,
  Motorcycle,
  Taxi,
} from '@phosphor-icons/react'
import clsx from 'clsx'
import { EmblaCarouselType } from 'embla-carousel'
import useEmblaCarousel from 'embla-carousel-react'
import Link from 'next/link'
import { useCallback, useEffect, useState } from 'react'
import { Website } from '@/src/@types/blinkadmin'
import { PageInfo } from '@/src/@types/models'
import { OtherLocationsBlock } from '@/src/@types/statamic'
import { get } from '@/src/BlinkAdminApiClient'
import Button from '@/src/components/core/Button'
import ConditionalWrapper from '@/src/components/core/ConditionalWrapper'
import TrailerIcon from '@/src/components/core/icons/TrailerIcon'
import Picture from '@/src/components/elements/Picture'

export async function getStaticProps({
  title,
  locations_set,
}: OtherLocationsBlock) {
  const locationIds = locations_set
    .map((locations) => locations.location.value)
    .join(',')
  const locations = await get<Website.Location[]>(
    `/website/v2/locations?filter[id]=${locationIds}`
  )

  return {
    title,
    locations: locations.map((location) => ({
      image: locations_set.find((loc) => loc.location.value === location.id)!
        .image,
      carUrl:
        location.services.find((service) => service.type === 'car')?.url ??
        null,
      ...location,
      services: location.services.filter(
        (service) => service.url && service.type !== 'car'
      ) as { type: string; url: string }[],
    })),
  }
}

export default function Component({
  title,
  locations,
  pageInfo,
}: Awaited<ReturnType<typeof getStaticProps>> & {
  pageInfo: PageInfo
}) {
  const [emblaRef, emblaApi] = useEmblaCarousel({
    skipSnaps: true,
    breakpoints: {
      '(min-width: 1024px)': {
        active: locations.length > 4 ? true : false,
        align: 'start',
      },
    },
  })
  const [currentIndex, setCurrentIndex] = useState(0)
  const [prevDisabled, setPrevDisabled] = useState(true)
  const [nextDisabled, setNextDisabled] = useState(true)
  const onSelect = useCallback((emblaApi: EmblaCarouselType) => {
    setCurrentIndex(emblaApi.selectedScrollSnap())
    setPrevDisabled(!emblaApi.canScrollPrev())
    setNextDisabled(!emblaApi.canScrollNext())
  }, [])

  useEffect(() => {
    if (!emblaApi) return

    onSelect(emblaApi)
    emblaApi.on('reInit', onSelect).on('select', onSelect)
  }, [emblaApi, onSelect])

  const prev = useCallback(() => {
    if (!emblaApi) return
    emblaApi?.plugins()?.autoplay?.stop()
    emblaApi.scrollPrev()
  }, [emblaApi])

  const next = useCallback(() => {
    if (!emblaApi) return
    emblaApi?.plugins()?.autoplay?.stop()
    emblaApi.scrollNext()
  }, [emblaApi])

  return (
    <div
      className={clsx({
        'mt-20 md:mt-32': pageInfo.newSpacing,
      })}
    >
      <div className="mx-auto max-w-container px-5 md:px-6">
        <h2 className="text-4xl font-black md:text-20xl">{title}</h2>
      </div>
      <div className="mt-6 overflow-hidden md:mt-10" ref={emblaRef}>
        <div className="flex">
          {locations.map((location) => (
            <div
              key={location.id}
              className="min-w-0 flex-[0_0_calc(100%-12px)] border-r border-gray-150 px-5 last:border-none min-[1342px]:first:ml-[calc((100%-1320px)/2)] min-[1342px]:last:mr-[calc((100%-1320px)/2)]! lg:flex-[0_0_calc(min(25%,1320px/4))]"
            >
              <ConditionalWrapper
                when={location.carUrl}
                wrapper={(children, carUrl) => (
                  <Link href={carUrl} className="group">
                    {children}
                  </Link>
                )}
              >
                <div className="relative aspect-16/9 w-full overflow-hidden rounded-xl border border-gray-150 md:rounded-lg">
                  <Picture
                    {...location.image}
                    sizes="320px"
                    fill
                    className="object-cover transition-transform duration-300 group-hover:scale-105"
                  />
                  <Button
                    as="div"
                    version="white-gray"
                    size="icon-small"
                    icon={<ArrowUpRight weight="bold" />}
                    className="absolute top-3 right-3"
                  />
                </div>
                <h3 className="mt-4 text-lg font-black">{location.name}</h3>
              </ConditionalWrapper>
              <p className="mt-1 text-gray-800">
                {location.email}
                <br />
                {location.phone}
              </p>
              <p className="mt-4 text-sm text-gray-600">Weitere Angebote</p>
              <div className="mt-2 flex flex-wrap gap-2">
                {location.services.map((service) => (
                  <ServiceTag key={service.type} {...service} />
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
      <div
        className={clsx(
          'mx-auto mt-6 flex max-w-container items-center justify-between px-5 md:justify-end md:px-6',
          {
            'lg:hidden': prevDisabled && nextDisabled,
          }
        )}
      >
        <p className="text-sm text-gray-600 lg:hidden">
          {currentIndex + 1} von {locations.length}
        </p>
        <div className="justify-end space-x-3">
          <Button
            size="icon-small"
            icon={<CaretLeft weight="bold" />}
            onClick={prev}
            version="gray-outlined"
            disabled={prevDisabled}
          />
          <Button
            size="icon-small"
            icon={<CaretRight weight="bold" />}
            onClick={next}
            version="gray-outlined"
            disabled={nextDisabled}
          />
        </div>
      </div>
    </div>
  )
}

const ServiceTag = ({ type, url }: { type: string; url: string }) => {
  let label = type
  let Icon = Car
  switch (type) {
    case 'bike':
      label = 'Motorrad'
      Icon = Motorcycle
      break
    case 'firstaid':
      label = 'Nothilfekurs'
      Icon = FirstAidKit
      break
    case 'vku':
      label = 'VKU'
      Icon = GraduationCap
      break
    case 'trailer':
      label = 'Anhänger'
      Icon = TrailerIcon
      break
    case 'control':
      label = 'Kontrollfahrt'
      Icon = Car
      break
    case 'taxi':
      label = 'Taxi'
      Icon = Taxi
      break
  }

  return (
    <Link
      href={url}
      className="flex items-center space-x-2 rounded-full border border-gray-300 py-2 pr-4 pl-3 text-sm transition-colors duration-200 hover:bg-gray-50 hover:duration-150"
    >
      <Icon size={20} weight="bold" className="text-gray-600" />
      <span className="text-gray-800">{label}</span>
    </Link>
  )
}
